import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Alert from "@mui/material/Alert";
import { decoratedFetch } from "../../req_utils";
import { SUPPORT_EMAIL } from "../../constants";

const AdjustInventoryDialog = ({
  isOpen,
  inventoryItem,
  handleClose,
  setSnackbarOpen,
  setSnackbarMessage,
}) => {
  const [direction, setDirection] = useState("in");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setDirection("in");
    setAmount("");
    setNotes("");
    setAlertMessage("");
    setLoading(false);
  }, [isOpen]);

  const unitName = inventoryItem?.size_unit?.name || "";
  const currentQuantity = parseFloat(inventoryItem?.quantity) || 0;
  const parsedAmount = parseFloat(amount) || 0;
  const resultingQuantity =
    direction === "in"
      ? currentQuantity + parsedAmount
      : currentQuantity - parsedAmount;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!parsedAmount) {
      setAlertMessage("Enter a quantity to add or remove");
      return;
    }
    if (!notes.trim()) {
      setAlertMessage("A reason is required so the change can be traced later");
      return;
    }

    setLoading(true);
    decoratedFetch(`/adjust_inventory_item/${inventoryItem.id}`, {
      method: "POST",
      body: JSON.stringify({
        quantity_delta:
          direction === "in" ? `${parsedAmount}` : `-${parsedAmount}`,
        notes: notes,
      }),
    })
      .then((response) => {
        if (response.status === 200) {
          return;
        }
        if (response.status === 400) {
          return response.json().then((errorPayload) => {
            setAlertMessage(errorPayload.msg || "Invalid adjustment");
            throw new Error();
          });
        }
        setAlertMessage(
          `Internal server error. If this persists, please contact ${SUPPORT_EMAIL}`,
        );
        throw new Error();
      })
      .then(() => {
        setLoading(false);
        handleClose();
        setSnackbarOpen(true);
        setSnackbarMessage("Stock adjusted");
      })
      .catch((error) => {
        console.error(`Error adjusting inventory: ${error}`);
        setLoading(false);
      });
  };

  return (
    <Dialog
      disableScrollLock
      open={isOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": { minWidth: "500px" },
      }}
    >
      <DialogTitle sx={{ fontSize: "22px", paddingBottom: "0px" }}>
        Adjust Stock
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} direction="row" sx={{ marginTop: "2px" }}>
          {alertMessage && (
            <Grid item xs={12}>
              <Alert
                onClose={() => setAlertMessage("")}
                severity="error"
                sx={{ width: "100%", borderRadius: 8 }}
              >
                {alertMessage}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2">
              {inventoryItem?.name} currently has{" "}
              <b>
                {currentQuantity} {unitName}
              </b>{" "}
              on hand.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={direction}
              onChange={(event, newDirection) =>
                newDirection && setDirection(newDirection)
              }
            >
              <ToggleButton value="in">Add Stock</ToggleButton>
              <ToggleButton value="out">Remove Stock</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={`Quantity${unitName ? ` (${unitName})` : ""}`}
              type="number"
              fullWidth
              value={amount}
              onChange={(e) => {
                if (parseFloat(e.target.value) < 0) {
                  return;
                }
                setAmount(e.target.value);
              }}
              helperText={
                parsedAmount
                  ? `New on hand: ${resultingQuantity} ${unitName}`
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason"
              required
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Received PO 1042, damaged in transit, annual recount"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: "flex-end", margin: "5px", marginRight: "15px" }}
      >
        {loading ? (
          <CircularProgress
            size="25px"
            sx={{ textAlign: "center", mt: "6px", mb: "5.5px", mr: "20px" }}
          />
        ) : (
          <div>
            <Button
              sx={{ marginRight: "10px" }}
              variant="outlined"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Apply
            </Button>
          </div>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdjustInventoryDialog;
